export interface CallToActionNavigation {
  url: string;
  target: '_self' | '_blank';
}

/**
 * Normalize a user-entered URL by prepending the right protocol when missing.
 * - Empty / already-schemed values are returned untouched.
 * - When `type` is `'Email'` / `'Phone'`, falls back to `mailto:` / `tel:`.
 * - Otherwise (or when `type` is omitted), falls back to `https://`.
 *
 * Single source of truth: experience-viewer's helpers.ts re-exports this
 * rather than keeping its own copy (adapters -> experience-viewer would be
 * a circular dependency; experience-viewer -> adapters is the allowed
 * direction, and is already established via buildExperienceAssets).
 */
export const ensureProtocol = (
  url: string,
  type?: 'Email' | 'Phone',
): string => {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)) return trimmed;
  if (type === 'Email') return `mailto:${trimmed}`;
  if (type === 'Phone') return `tel:${trimmed}`;
  return `https://${trimmed}`;
};

/**
 * Resolve a CallToAction's `action` into a navigable URL + window.open
 * target. Mirrors the classic experience-viewer's CallToActionList dispatch
 * (libs/experience-viewer/src/lib/components/CallToActions.tsx): a
 * DocumentDownload action opens the publication's main document path,
 * Email/Phone get mailto:/tel: prefixes, everything else opens as a link.
 * Pure and SSR-safe so it can run server-side (sales) or client-side (admin).
 */
export function resolveCallToActionUrl(
  action: { type: string; url?: string | null },
  documentPath?: string | null,
): CallToActionNavigation | undefined {
  let url: string | undefined;
  if (action.type === 'DocumentDownload') {
    url = documentPath ?? undefined;
  } else if (action.url) {
    const protocolHint =
      action.type === 'Email' || action.type === 'Phone'
        ? action.type
        : undefined;
    url = ensureProtocol(action.url, protocolHint);
  }
  if (!url) return undefined;
  return { url, target: action.type === 'Email' ? '_self' : '_blank' };
}
